import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Quill from 'quill';

const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
// only formats available in toolbar, everything else (colors, fonts, sizes, headings...) is dropped on paste
const ALLOWED_FORMATS = ['bold', 'italic', 'underline', 'align', 'list', 'link', 'image'];

/**
 * Rich text editor (Quill 2) usable as a form control, value is HTML.
 * ngx-quill is not used because its Quill 2 versions require Angular 17+.
 */
@Component({
  selector: 'app-quill-editor',
  templateUrl: './quill-editor.component.html',
  styleUrls: ['./quill-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuillEditorComponent),
      multi: true,
    },
  ],
})
export class QuillEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {

  @Input() placeholder: string = '';
  /** Uploads image and resolves with its url. When not set, image button is hidden and pasted images are ignored. */
  @Input() imageUploadHandler?: (file: File) => Promise<string | undefined>;

  @ViewChild('editor', { static: true }) editorElement: ElementRef<HTMLElement>;

  private quill: Quill;
  private value: string = '';
  private changeFn: (value: string) => void = () => {};
  private touchFn: () => void = () => {};

  ngAfterViewInit(): void {
    this.quill = new Quill(this.editorElement.nativeElement, {
      theme: 'snow',
      placeholder: this.placeholder,
      formats: ALLOWED_FORMATS,
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ align: [] }],
            [{ list: 'bullet' }, { list: 'ordered' }],
            this.imageUploadHandler ? ['link', 'image'] : ['link'],
            ['clean'],
          ],
          handlers: {
            image: () => this.selectImage(),
          },
        },
        uploader: {
          mimetypes: IMAGE_MIME_TYPES,
          handler: (range: { index: number }, files: File[]) => this.uploadImages(range.index, files),
        },
      },
    });

    this.setEditorContent(this.value);

    this.quill.on('text-change', () => {
      this.value = this.getHtml();
      this.changeFn(this.value);
    });
    this.quill.on('selection-change', (range) => {
      if (!range) {
        this.touchFn();
      }
    });
  }

  ngOnDestroy(): void {
    this.quill?.off('text-change');
    this.quill?.off('selection-change');
  }

  writeValue(value: string): void {
    this.value = value ?? '';
    if (this.quill) {
      this.setEditorContent(this.value);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.changeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.touchFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.quill?.enable(!isDisabled);
  }

  private setEditorContent(html: string): void {
    const delta = this.quill.clipboard.convert({ html });
    this.quill.setContents(delta, 'silent');
  }

  private getHtml(): string {
    const isEmpty = this.quill.getText().trim() === '' && !this.quill.root.querySelector('img');
    if (isEmpty) {
      return '';
    }

    return this.fixNonBreakingSpaces(this.fixListAlignment(this.quill.getSemanticHTML()));
  }

  /**
   * Workaround for Quill 2 bug where getSemanticHTML() converts every space to &nbsp;,
   * which prevents text from wrapping when rendered (e.g. on reception kiosk)
   */
  private fixNonBreakingSpaces(html: string): string {
    return html.replace(/&nbsp;| /g, ' ');
  }

  /**
   * Workaround for Quill 2 bug where getSemanticHTML() drops ql-align-* classes from <li> elements
   */
  private fixListAlignment(html: string): string {
    const domItems: Element[] = Array.from(this.quill.root.querySelectorAll('li'));
    if (!domItems.length) {
      return html;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const semanticItems = Array.from(doc.querySelectorAll('li'));
    const alignClasses = ['ql-align-center', 'ql-align-right', 'ql-align-justify'];

    domItems.forEach((domItem, i) => {
      const semanticItem = semanticItems[i];
      if (!semanticItem) {
        return;
      }
      alignClasses
        .filter(cls => domItem.classList.contains(cls))
        .forEach(cls => semanticItem.classList.add(cls));
    });

    return doc.body.innerHTML;
  }

  private selectImage(): void {
    if (!this.imageUploadHandler) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = IMAGE_MIME_TYPES.join(',');
    input.onchange = () => {
      const range = this.quill.getSelection(true);
      this.uploadImages(range.index, Array.from(input.files ?? []));
    };
    input.click();
  }

  private async uploadImages(index: number, files: File[]): Promise<void> {
    if (!this.imageUploadHandler) {
      return;
    }

    for (const file of files.filter(f => IMAGE_MIME_TYPES.includes(f.type))) {
      const url = await this.imageUploadHandler(file);
      if (url) {
        this.quill.insertEmbed(index, 'image', url, 'user');
        index++;
      }
    }
    this.quill.setSelection(index, 0, 'silent');
  }
}
